// invest_FE/src/pages/Admin/EventViewer.js
import React, { useState, useEffect, useCallback, useContext } from 'react';
import { NotificationContext } from '../../context/NotificationContext';
import { UserContext } from '../../context/UserContext';
import { getEvents } from '../../services/adminService';

const EventViewer = () => {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { addNotification } = useContext(NotificationContext);
  const [selectedEvent, setSelectedEvent] = useState(null); // State for the modal
  const { user } = useContext(UserContext);

  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalEvents: 0,
    limit: 50,
  });
  // --- State for Filters ---
  const [filters, setFilters] = useState({
    eventType: '',
    severity: '',
    user: '',
    ipAddress: '',
    startDate: '',
    endDate: '',
  });
  const [activeFilters, setActiveFilters] = useState(filters);

  // --- State for Sorting ---
  const [sortConfig, setSortConfig] = useState({ key: 'timestamp', direction: 'desc' });

  const fetchEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { page: pagination.currentPage, limit: pagination.limit, sortBy: sortConfig.key, sortOrder: sortConfig.direction };
      for (const key in activeFilters) {
        if (activeFilters[key]) {
          params[key] = activeFilters[key];
        }
      }

      const response = await getEvents(params, user.token);
      setEvents(response.events);
      setPagination((prev) => ({
        ...prev,
        currentPage: response.currentPage,
        totalPages: response.totalPages,
        totalEvents: response.totalEvents,
      }));
    } catch (err) {
      setError(err.message);
      addNotification(`Error fetching events: ${err.message}`, 'error');
    } finally {
      setLoading(false);
    }
  }, [addNotification, activeFilters, user.token, pagination.currentPage, pagination.limit, sortConfig]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleApplyFilters = () => {
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    setActiveFilters(filters);
  };

  const handleClearFilters = () => {
    const clearedFilters = { eventType: '', severity: '', user: '', ipAddress: '', startDate: '', endDate: '' };
    setFilters(clearedFilters);
    setPagination((prev) => ({ ...prev, currentPage: 1 }));
    setActiveFilters(clearedFilters);
  };

  const handleNextPage = () => {
    setPagination((prev) => ({ ...prev, currentPage: Math.min(prev.currentPage + 1, prev.totalPages) }));
  };

  const handlePrevPage = () => {
    setPagination((prev) => ({ ...prev, currentPage: Math.max(prev.currentPage - 1, 1) }));
  };

  // --- Sorting Handler ---
  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const getSortIndicator = (key) => {
    if (sortConfig.key !== key) return null;
    return sortConfig.direction === 'asc' ? ' ▲' : ' ▼';
  };

  const severityOptions = ['INFO', 'WARN', 'ERROR', 'CRITICAL'];

  // Basic modal styles - can be moved to a CSS file later
  const modalBackdropStyle = {
    position: 'fixed',
    top: 0,
    left: 0,
    width: '100%',
    height: '100%',
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
  };

  const modalContentStyle = {
    backgroundColor: 'white',
    padding: '20px',
    borderRadius: '5px',
    maxWidth: '80%',
    maxHeight: '80%',
    overflow: 'auto',
  };

  const getSeverityStyle = (severity) => {
    const baseStyle = {
      padding: '4px 8px',
      borderRadius: '4px',
      display: 'inline-block',
      color: 'white',
      fontWeight: 'bold',
      textAlign: 'center',
      minWidth: '70px',
    };
    switch (severity) {
      case 'CRITICAL':
        return { ...baseStyle, backgroundColor: '#b71c1c' };
      case 'ERROR':
        return { ...baseStyle, backgroundColor: '#d32f2f' };
      case 'WARN':
        return { ...baseStyle, backgroundColor: '#ffc107', color: 'black' };
      case 'INFO':
        return { ...baseStyle, backgroundColor: '#1976d2' };
      default:
        return {};
    }
  };

  // --- Style for sortable headers ---
  const sortableHeaderStyle = {
    cursor: 'pointer',
    userSelect: 'none',
  };

  return (
    <div className="event-viewer-container" style={{ padding: '20px' }}>
      <h2>System Event Viewer</h2>
      {/* --- Filter Bar --- */}
      <div className="filter-bar" style={{ display: 'flex', flexWrap: 'wrap', gap: '10px', alignItems: 'center', padding: '10px', border: '1px solid #ccc', borderRadius: '5px', marginBottom: '20px' }}>
        <input type="text" name="eventType" placeholder="Event Type" value={filters.eventType} onChange={handleFilterChange} style={{ padding: '8px' }} />
        <select name="severity" value={filters.severity} onChange={handleFilterChange} style={{ padding: '8px' }}>
          <option value="">All Severities</option>
          {severityOptions.map((opt) => (
            <option key={opt} value={opt}>
              {opt}
            </option>
          ))}
        </select>
        <input type="text" name="user" placeholder="User ID" value={filters.user} onChange={handleFilterChange} style={{ padding: '8px' }} />
        <input type="text" name="ipAddress" placeholder="IP Address" value={filters.ipAddress} onChange={handleFilterChange} style={{ padding: '8px' }} />
        <input type="date" name="startDate" value={filters.startDate} onChange={handleFilterChange} style={{ padding: '8px' }} />
        <input type="date" name="endDate" value={filters.endDate} onChange={handleFilterChange} style={{ padding: '8px' }} />
        <button onClick={handleApplyFilters} style={{ padding: '8px 12px', cursor: 'pointer' }}>
          Apply
        </button>
        <button onClick={handleClearFilters} style={{ padding: '8px 12px', cursor: 'pointer' }}>
          Clear
        </button>
      </div>
      {loading && <div>Loading...</div>}
      {error && <div style={{ color: 'red' }}>Error: {error}</div>}
      {!loading && !error && (
        <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px' }}>
          <thead>
            <tr style={{ borderBottom: '2px solid #333' }}>
              <th style={{ ...sortableHeaderStyle, textAlign: 'left', padding: '8px' }} onClick={() => handleSort('timestamp')}>
                Timestamp{getSortIndicator('timestamp')}
              </th>
              <th style={{ ...sortableHeaderStyle, textAlign: 'left', padding: '8px' }} onClick={() => handleSort('eventType')}>
                Type{getSortIndicator('eventType')}
              </th>
              <th style={{ ...sortableHeaderStyle, textAlign: 'left', padding: '8px' }} onClick={() => handleSort('severity')}>
                Severity{getSortIndicator('severity')}
              </th>
              <th style={{ ...sortableHeaderStyle, textAlign: 'left', padding: '8px' }} onClick={() => handleSort('user')}>
                User{getSortIndicator('user')}
              </th>
              <th style={{ ...sortableHeaderStyle, textAlign: 'left', padding: '8px' }} onClick={() => handleSort('ipAddress')}>
                IP Address{getSortIndicator('ipAddress')}
              </th>
              <th style={{ textAlign: 'left', padding: '8px' }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {events.map((event) => (
              <tr key={event._id} style={{ borderBottom: '1px solid #ddd' }}>
                <td style={{ padding: '8px' }}>{new Date(event.timestamp).toLocaleString()}</td>
                <td style={{ padding: '8px' }}>{event.eventType}</td>
                <td style={{ padding: '8px' }}>
                  <span style={getSeverityStyle(event.severity)}>{event.severity}</span>
                </td>
                <td style={{ padding: '8px' }}>{event.user?.username || 'N/A'}</td>
                <td style={{ padding: '8px' }}>{event.ipAddress || 'N/A'}</td>
                <td style={{ padding: '8px' }}>
                  <button onClick={() => setSelectedEvent(event)} style={{ padding: '4px 8px', cursor: 'pointer' }}>
                    View Details
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {!loading && events.length === 0 && !error && <p style={{ marginTop: '20px' }}>No events found for the current filters.</p>}

      {/* Pagination Controls */}
      {!loading && pagination.totalPages > 1 && (
        <div className="pagination-controls" style={{ marginTop: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button onClick={handlePrevPage} disabled={pagination.currentPage === 1} style={{ padding: '8px 12px', cursor: 'pointer' }}>
            Previous
          </button>
          <span>
            Page {pagination.currentPage} of {pagination.totalPages} ({pagination.totalEvents} total events)
          </span>
          <button onClick={handleNextPage} disabled={pagination.currentPage === pagination.totalPages} style={{ padding: '8px 12px', cursor: 'pointer' }}>
            Next
          </button>
        </div>
      )}

      {/* Modal JSX */}
      {selectedEvent && (
        <div style={modalBackdropStyle} onClick={() => setSelectedEvent(null)}>
          <div style={modalContentStyle} onClick={(e) => e.stopPropagation()}>
            <h3>Event Details (ID: {selectedEvent._id})</h3>
            <pre>
              <code>{JSON.stringify(selectedEvent.details, null, 2)}</code>
            </pre>
            <button onClick={() => setSelectedEvent(null)} style={{ marginTop: '15px', padding: '8px 12px', cursor: 'pointer' }}>
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default EventViewer;
